import { of, concat, race, zip } from 'rxjs'
import { map, concatMap, switchMap, flatMap } from 'rxjs/operators'
import { mapObjIndexed } from 'ramda'

import { createStoreNavigateTo$ } from './navigation'
import {
  createClearLoading$,
  createLoading$,
} from '@apiEnabler/api-enabler-common/lib/loading'
import {
  createModal$,
  clearModal$,
} from '@apiEnabler/api-enabler-common/lib/dialogs'

import {
  updateSweepingStateAfterLoad,
  updateSweepingHistoryState,
  updateSweepingActivitiesState,
  updateSweepingGeneratedState$,
} from '@apiEnabler/ob-common/lib/sweeping-consents'

import { initialData$ } from './customer'
import {
  displaySweeping$,
  displaySweepingForTpp$,
  displaySweepingDetails$,
  ceaseCancelSweeping$,
  confirmCancelSweeping$,
  cancelSweeping$,
} from './intents'
import {
  createCancelConsent$,
  createGetConsentAudits$,
  createGetActivities$,
} from './services'
import { isStaffMember } from './staff'

const addKeyAsNameProperty = mapObjIndexed((value, key) => ({
  ...value,
  name: key,
}))

let order = 0

export const SWEEPING_SECTIONS = addKeyAsNameProperty({
  active: {
    label: 'Active',
    description:
      'You’ve confirmed permission to a service provider to sweep funds from your own accounts for an agreed period. You can cancel this permission at any time.',
    order: order++,
  },
  deleted: {
    label: 'Deleted',
    description: 'You cancelled this permission through the service provider.',
    order: order++,
  },
  expired: {
    label: 'Expired',
    description:
      'The permission changed from active to expired as it surpassed the agreed period.',
    order: order++,
  },
  revoked: {
    label: 'Revoked',
    description:
      'The service provider has been revoked by the Financial Conduct Authority (FCA). This cannot be undone.',
    order: order++,
  },
  suspended: {
    label: 'Suspended',
    description:
      'To protect the Bank and its customers, the Bank may temporarily suspend a service provider. Once the reason(s) for this suspension has been resolved, the connection will be automatically reinstated by the Bank.',
    order: order++,
  },
  withdrawn: {
    label: 'Cancelled',
    description:
      'You cancelled this permission through our mobile app or online banking (via Connections).',
    order: order++,
  },
  ...(isStaffMember
    ? {
        blocked: {
          label: 'Blocked',
          description:
            'This status is not displayed to the customer. \n\n A customer has started the process to give permission to a service provider for an agreed period. This agreement request has been blocked. This state occurs where there are creditable reasons and/or concerns that mean allowing permission could potentially be harmful to the Bank and customer and is therefore denied.',
          order: order++,
          info: 'Only visible to staff',
        },
        declined: {
          label: 'Declined',
          description:
            'This status is not displayed to the customer. \n\n A customer has started the process to give permission to a service provider to payment accounts for an agreed period but has cancelled this authorisation process.',
          order: order++,
          info: 'Only visible to staff',
        },
        created: {
          label: 'Created',
          description:
            'The customer has provided permission to a service provider to have access to specified account(s) for an agreed period. It is in the process of being authorised. Once authorised it will show as active. This state is only viewable to staff.',
          order: order++,
          info: 'Only visible to staff',
        },
      }
    : {}),
})

export function categoriseSweeping(consent) {
  switch (consent.grouping) {
    case 'ACTIVE':
      return consent.status === 'ACCEPTED'
        ? isStaffMember
          ? SWEEPING_SECTIONS.created
          : null
        : SWEEPING_SECTIONS.active
    case 'DELETED':
      return SWEEPING_SECTIONS.deleted
    case 'EXPIRED':
      return SWEEPING_SECTIONS.expired
    case 'TPP_REVOKED':
      return SWEEPING_SECTIONS.revoked
    case 'TPP_SUSPENDED':
    case 'TPP_CLIENT_SUSPENDED':
      return SWEEPING_SECTIONS.suspended
    case 'WITHDRAWN':
      return SWEEPING_SECTIONS.withdrawn
    case 'BLOCKED':
      return isStaffMember ? SWEEPING_SECTIONS.blocked : null
    case 'DECLINED':
      return isStaffMember ? SWEEPING_SECTIONS.declined : null
    default:
      return null // We don't show any consents not grouped by the above grouping
  }
}

export const sweepingDisplay$ = displaySweeping$.pipe(
  switchMap(() =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading third parties with payments'),
      createStoreNavigateTo$('/recurring-payments/sweeping/tpps'),
      updateSweepingGeneratedState$,
      createClearLoading$('Loading third parties with payments')
    )
  )
)

export const sweepingForTppDisplay$ = displaySweepingForTpp$.pipe(
  switchMap(clientId =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading payments for service provider'),
      createStoreNavigateTo$(
        `/recurring-payments/sweeping/tpps/${clientId}/payments`
      ),
      updateSweepingGeneratedState$,
      createClearLoading$('Loading payments for service provider')
    )
  )
)

function createLoadSweepingHistory$(consentId) {
  return zip(
    createGetConsentAudits$(consentId),
    createGetActivities$(consentId)
  ).pipe(
    concatMap(([consentAudits, activities]) =>
      of(
        updateSweepingHistoryState(consentId, consentAudits),
        updateSweepingActivitiesState(consentId, activities)
      )
    )
  )
}

function createCancelSweeping$(consent) {
  return createCancelConsent$(consent).pipe(
    map(consent => consent.set('grouping', consent.get('status'))),
    concatMap(consent =>
      createGetConsentAudits$(consent.get('id')).pipe(
        map(consentAudits =>
          updateSweepingStateAfterLoad(consent, consentAudits)
        )
      )
    )
  )
}

export const sweepingDetailsDisplay$ = displaySweepingDetails$.pipe(
  switchMap(({ clientId, consentId }) =>
    concat(
      initialData$,
      updateInitialData$,
      createLoading$('Loading payment details'),
      createStoreNavigateTo$(
        `/recurring-payments/sweeping/tpps/${clientId}/payments/${consentId}`
      ),
      updateSweepingGeneratedState$,
      createLoadSweepingHistory$(consentId),
      createClearLoading$('Loading payment details')
    )
  )
)

export const sweepingCancel$ = cancelSweeping$.pipe(
  flatMap(consent =>
    concat(
      createModal$('sweeping-cancel.confirm', consent),
      race(
        concat(
          confirmCancelSweeping$,
          createModal$('sweeping-cancel.loading'),
          createCancelSweeping$(consent),
          clearModal$
        ),
        concat(ceaseCancelSweeping$, clearModal$)
      )
    )
  )
)

const updateInitialData$ = of(state =>
  state.updateIn(['sweeping', 'index'], consents =>
    consents.filter(consent => categoriseSweeping(consent) !== null)
  )
)
